/**
 * Browser location adapter for the worker web/PWA client.
 *
 * This adapter ONLY captures coordinates and hands them to the existing worker
 * APIs. It never decides punch eligibility. Geofence evaluation, assignment
 * ownership, auto-punch decisions, and evidence storage all remain backend
 * authoritative — the accuracy and staleness rules below exist so the client
 * does not submit obviously unusable evidence, not to pre-approve a punch.
 *
 * iOS/Safari limitation: when the browser or Home Screen app is suspended, the
 * platform provides no guaranteed execution, so this adapter makes no promise
 * of continuous background breadcrumbs or background geofence evaluation. It
 * works while the app is foregrounded, which is all a punch needs.
 */

export type WorkerLocationErrorReason =
  | 'unsupported'
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout'
  | 'stale'
  | 'inaccurate'

export class WorkerLocationError extends Error {
  constructor(readonly reason: WorkerLocationErrorReason, message: string) {
    super(message)
    this.name = 'WorkerLocationError'
  }
}

export interface WorkerLocationFix {
  latitude: number
  longitude: number
  accuracyMeters: number | null
  capturedAt: number
  /** Age of the fix at the moment it was accepted, in milliseconds. */
  ageMs: number
}

export interface WorkerLocationOptions {
  enableHighAccuracy?: boolean
  timeoutMs?: number
  maximumAgeMs?: number
  /** Reject fixes older than this. Defaults to 2 minutes. */
  maxFixAgeMs?: number
  /** Reject fixes coarser than this. `null` accepts any reported accuracy. */
  maxAccuracyMeters?: number | null
}

const DEFAULTS: Required<Omit<WorkerLocationOptions, 'maxAccuracyMeters'>> & { maxAccuracyMeters: number | null } = {
  enableHighAccuracy: true,
  timeoutMs: 20_000,
  maximumAgeMs: 0,
  maxFixAgeMs: 120_000,
  maxAccuracyMeters: null,
}

const errorFor = (error: GeolocationPositionError): WorkerLocationError => {
  if (error.code === error.PERMISSION_DENIED) {
    return new WorkerLocationError('permission_denied', 'Location permission was denied in this browser.')
  }
  if (error.code === error.TIMEOUT) {
    return new WorkerLocationError('timeout', 'The browser did not return a location in time.')
  }
  return new WorkerLocationError('position_unavailable', 'The browser could not determine a location.')
}

/**
 * Validate a raw browser position. Exported separately so the staleness and
 * accuracy rules can be asserted without a live geolocation provider.
 */
export function acceptFix(
  position: Pick<GeolocationPosition, 'coords' | 'timestamp'>,
  options: WorkerLocationOptions = {},
  now = Date.now()
): WorkerLocationFix {
  const resolved = { ...DEFAULTS, ...options }
  const capturedAt = Number.isFinite(position.timestamp) ? position.timestamp : now
  const ageMs = Math.max(0, now - capturedAt)
  if (resolved.maxFixAgeMs > 0 && ageMs > resolved.maxFixAgeMs) {
    throw new WorkerLocationError('stale', 'The browser returned an out-of-date location.')
  }
  const accuracy = Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null
  if (resolved.maxAccuracyMeters !== null && accuracy !== null && accuracy > resolved.maxAccuracyMeters) {
    throw new WorkerLocationError('inaccurate', 'The browser location is not accurate enough to use.')
  }
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracyMeters: accuracy,
    capturedAt,
    ageMs,
  }
}

export function geolocationSupported(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.geolocation)
}

export async function getCurrentWorkerLocation(options: WorkerLocationOptions = {}): Promise<WorkerLocationFix> {
  if (!geolocationSupported()) {
    throw new WorkerLocationError('unsupported', 'This browser does not provide location.')
  }
  const resolved = { ...DEFAULTS, ...options }
  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, (error) => reject(errorFor(error)), {
      enableHighAccuracy: resolved.enableHighAccuracy,
      timeout: resolved.timeoutMs,
      maximumAge: resolved.maximumAgeMs,
    })
  })
  return acceptFix(position, options)
}

export interface WorkerLocationWatch {
  stop: () => void
}

/**
 * Foreground breadcrumb watch. Callers must stop it when the worker leaves the
 * screen — the browser gives no guarantee once the tab is backgrounded, and
 * pretending otherwise would be a false promise on iOS in particular.
 */
export function watchWorkerLocation(
  onFix: (fix: WorkerLocationFix) => void,
  onError: (error: WorkerLocationError) => void,
  options: WorkerLocationOptions = {}
): WorkerLocationWatch {
  if (!geolocationSupported()) {
    onError(new WorkerLocationError('unsupported', 'This browser does not provide location.'))
    return { stop: () => undefined }
  }
  const resolved = { ...DEFAULTS, ...options }
  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      try {
        onFix(acceptFix(position, options))
      } catch (error) {
        if (error instanceof WorkerLocationError) onError(error)
      }
    },
    (error) => onError(errorFor(error)),
    {
      enableHighAccuracy: resolved.enableHighAccuracy,
      timeout: resolved.timeoutMs,
      maximumAge: resolved.maximumAgeMs,
    }
  )
  return { stop: () => navigator.geolocation.clearWatch(watchId) }
}

/**
 * Best-effort fix for an action that should still proceed without location
 * (the backend decides whether missing coordinates are acceptable).
 */
export async function tryGetCurrentWorkerLocation(
  options: WorkerLocationOptions = {}
): Promise<{ fix: WorkerLocationFix | null; error: WorkerLocationError | null }> {
  try {
    return { fix: await getCurrentWorkerLocation(options), error: null }
  } catch (error) {
    return { fix: null, error: error instanceof WorkerLocationError ? error : new WorkerLocationError('position_unavailable', 'Location is unavailable.') }
  }
}
